import type { CoreError } from '@ingenico/api-sdk-core/common';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { BasicDialogModal } from '@ingenico/react-ui/basic-dialog-modal';
import { CoreAlert } from '@ingenico/react-ui/core-alert';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { openParameterOutputListModalAtom } from '@ingenico/react-ui/parameter-output-section';
import { Divider, LinearProgress } from '@mui/material';
import { useAtom } from 'jotai';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModuleItemField } from '../../domain/models/snapshot.model';
import { useDeviceManagementError } from '../../hooks';
import { ParameterOutputFileModal } from '../parameter-output-file-modal/ParameterOutputFileModal.ctrl';
import styles from './ParameterOutputListModal.module.scss';
import { ModuleItem } from './components/ModuleItem.ctrl';

export interface UseParameterOutputListModalReturn {
  readonly parameterOutputListModal: ReactNode;
  readonly handleParameterOutputListModalOpen: () => void;
}
interface UseParameterOutputListModalProps {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: CoreError | null;
  readonly data: ModuleItemField[] | null;
  readonly deploymentUnitId: string;
}
enum LIFECYCLE {
  Loading = 0,
  Success = 1,
  Error = 2,
}

export function useParameterOutputListModal({
  isLoading,
  isError,
  error,
  data,
  deploymentUnitId,
}: UseParameterOutputListModalProps): UseParameterOutputListModalReturn {
  const [isModalOpen, setModalOpen] = useAtom(openParameterOutputListModalAtom);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [isParameterOutputFileModalOpen, setIsParameterOutputFileModal] = useState(false);
  const [selectedModuleItemField, setModuleItemField] = useState<ModuleItemField | undefined>(undefined);
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.CALL_LOGS]);

  const { getErrorMessage } = useDeviceManagementError();

  useEffect(() => {
    if (isModalOpen && data?.length === 1) {
      setSelectedItemId(data?.[0]?.configurationModuleId);
    }
  }, [data, isModalOpen]);

  const modalLifecycle = useMemo<LIFECYCLE>(() => {
    if (isLoading) {
      return LIFECYCLE.Loading;
    }
    if (isError) {
      return LIFECYCLE.Error;
    }

    return LIFECYCLE.Success;
  }, [isError, isLoading]);

  const errorMessage = useMemo(() => {
    if (error) {
      return getErrorMessage(error, t('sections.parameterOutput.error.default', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS }));
    }
    return t('sections.parameterOutput.error.default', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS });
  }, [error, getErrorMessage, t]);

  const handleModalOpen = useCallback(() => {
    setModalOpen(true);
  }, [setModalOpen]);
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);
  const onCleanParameterOutput = useCallback(() => {
    setSelectedItemId('');
    setModuleItemField(undefined);
  }, []);

  const onCancelParameterOutputModal = useCallback(() => {
    onCleanParameterOutput();
    handleModalClose();
    setIsParameterOutputFileModal(false);
  }, [handleModalClose, onCleanParameterOutput]);

  const onBackParameterOutputModal = useCallback(() => {
    handleModalOpen();
    setIsParameterOutputFileModal(false);
  }, [handleModalOpen]);

  const onResetModal = useCallback(() => {
    if (!isParameterOutputFileModalOpen) {
      onCleanParameterOutput();
    }
    handleModalClose();
  }, [handleModalClose, isParameterOutputFileModalOpen, onCleanParameterOutput]);

  const onCancel = useCallback(() => {
    onResetModal();
  }, [onResetModal]);

  const onViewOutput = useCallback(() => {
    setModuleItemField(data?.find((el) => el.configurationModuleId === selectedItemId));
    setIsParameterOutputFileModal(true);
    handleModalClose();
    //
  }, [data, handleModalClose, selectedItemId]);

  const parameterOutputListModal = useMemo(
    () => (
      <>
        <BasicDialogModal
          isOpen={isModalOpen}
          hasCloseIcon
          onIconClick={onResetModal}
          title={t('sections.parameterOutput.modal.title', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS })}
          maxWidth="md"
          actions={
            <div className={styles.modalActions}>
              <CoreButton onClick={onCancel} variant="text">
                {t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON })}
              </CoreButton>
              <CoreButton onClick={onViewOutput} variant="contained" disabled={selectedItemId === ''}>
                {t('sections.parameterOutput.modal.submit', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS })}
              </CoreButton>
            </div>
          }
        >
          <div>
            {modalLifecycle === LIFECYCLE.Loading && <LinearProgress />}
            {modalLifecycle === LIFECYCLE.Success &&
              data?.map((el, index) => {
                return (
                  <>
                    <ModuleItem
                      {...el}
                      onClick={() => setSelectedItemId(el.configurationModuleId)}
                      isSelected={selectedItemId === el.configurationModuleId}
                      key={el.configurationModuleId}
                    />
                    {index !== data.length - 1 && <Divider />}
                  </>
                );
              })}
            {modalLifecycle === LIFECYCLE.Error && (
              <div className={styles.errorAlertContainer}>
                <CoreAlert
                  severity={'Error'}
                  title={t('sections.parameterOutput.error.title', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS })}
                  message={errorMessage}
                />
              </div>
            )}
          </div>
        </BasicDialogModal>
        <ParameterOutputFileModal
          isOpen={isParameterOutputFileModalOpen}
          deploymentUnitId={deploymentUnitId}
          moduleItemField={selectedModuleItemField}
          onCancel={onCancelParameterOutputModal}
          onBack={onBackParameterOutputModal}
        />
      </>
    ),
    [
      data,
      deploymentUnitId,
      errorMessage,
      isModalOpen,
      isParameterOutputFileModalOpen,
      modalLifecycle,
      onBackParameterOutputModal,
      onCancel,
      onCancelParameterOutputModal,
      onResetModal,
      onViewOutput,
      selectedItemId,
      selectedModuleItemField,
      t,
    ],
  );

  return {
    parameterOutputListModal,
    handleParameterOutputListModalOpen: handleModalOpen,
  };
}
